/* eslint-disable @typescript-eslint/no-explicit-any */
import { types, Instance, flow } from 'mobx-state-tree';
import { AxiosResponse } from 'axios';
import axios from 'server';
import { DepartmentModel, IDepartmentModel, ManagerModel } from './models/department.model';
import { UserDepartmentsView, UserDepartmentData } from './types';
const { model, string, array, maybeNull, reference } = types;

export const DepartmentTableStore = model('DepartmentTableStore', {
  userDepartment: array(DepartmentModel),
  managerDepartment: array(ManagerModel),
  filter: string,
  clickedDepartmentUser: maybeNull(reference(DepartmentModel)),
})
  .views((self) => ({
    get userDepartmentsView(): UserDepartmentsView[] {
      if (!self.userDepartment || self.userDepartment.length === 0) {
        return [];
      }
      const usersView: UserDepartmentsView[] = [];
      for (let i = 0; i < self.userDepartment.length; i++) {
        const userDepartmentItem = self.userDepartment[i];
        const userView: UserDepartmentsView = { ...userDepartmentItem, id: i };
        usersView.push(userView);
      }
      if (!self.filter) {
        return usersView;
      }
      const regexp = new RegExp(self.filter, 'i');
      const filteredUserGroupsView = usersView.filter(({ mail, sn, uid, givenname }) =>
        regexp.test(`${mail}${sn}${uid}${givenname}`)
      );
      return filteredUserGroupsView;
    },
    get CurrentClickedUser() {
      return self.clickedDepartmentUser;
    },
  }))
  .actions((self) => ({
    clearClickedUser() {
      self.clickedDepartmentUser = null;
    },
  }))
  .actions((self) => ({
    normalizeuserDepartment({ data }: AxiosResponse): IDepartmentModel[] {
      self.userDepartment.replace(data.data);
      return self.userDepartment;
    },
    setFilter(filter: string) {
      self.filter = filter;
    },
    setClickedUser(dn: string): void {
      self.clickedDepartmentUser = self.userDepartment.find((user) => user.dn === dn) || null;
    },

    clearSelected(): void {
      self.clickedDepartmentUser = null;
    },
  }))
  .actions((self) => ({
    getUserDepartment: flow(function* (dn) {
      const result = yield axios
        .get<IDepartmentModel[]>(`departments/${dn}/users`)
        .then(self.normalizeuserDepartment)
        .catch((error) => {
          throw new Error(error.response.data.data.message);
        });
      return result;
    }),
    getManagerDepartment: flow(function* (dn) {
      try {
        const response = yield axios.get(`/departments/${dn}`);
        const department = response.data.data;
        const managers = department.managers ?? [];
        self.managerDepartment.replace(managers);
        return managers;
      } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message);
      }
    }),
    getAppDepartment: flow(function* (id) {
      try {
        const response = yield axios.get(`departments/${id}/apps`);
        const department = response.data.data;
        const apps = department.map((item: any) => item.provider);
        return apps;
      } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message);
      }
    }),
    deleteDepartment: flow(function* (data: UserDepartmentData, dn) {
      const result = yield axios
        .delete<IDepartmentModel>(`departments/${dn}/users`, { data })
        .then(self.clearSelected)
        .catch((error) => {
          throw new Error(error);
        });
      return result;
    }),
    deleteManager: flow(function* (data: UserDepartmentData) {
      try {
        const result = yield axios.delete<IDepartmentModel>(
          `departments/${data.department_id}/managers/${data.manager_id}`,
          {
            data: {
              manager_id: data.manager_id,
            },
          }
        );

        self.clearSelected();
        return result;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || error.message || 'Failed to remove manager');
      }
    }),
    deleteApp: flow(function* (data: { department_id: number; provider: string }) {
      try {
        const result = yield axios.delete(`departments/${data.department_id}/apps/${data.provider}`);

        self.clearSelected();
        return result;
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || error.message || 'Failed to remove app');
      }
    }),
  }));

export type IDepartmentTableStore = Instance<typeof DepartmentTableStore>;
